import { useState } from "react";
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, IconButton, MenuItem, Paper, Stack, Switch, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { carriersApi, vehiclesApi } from "@/api/endpoints";
import { errorMessage } from "@/api/client";

interface FormState {
  id?: string; registrationNo: string;
  carrierId: string; capacityKg: number; coldChainCapable: boolean;
  insuranceExpiry: string; isActive: boolean;
}
const blank = (): FormState => ({
  registrationNo: "", carrierId: "", capacityKg: 0, coldChainCapable: false,
  insuranceExpiry: "", isActive: true,
});

export function VehiclesPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(blank());
  const [error, setError] = useState<string | null>(null);

  const carriers = useQuery({ queryKey: ["carriers"], queryFn: () => carriersApi.list({ pageSize: 200 }) });
  const query = useQuery({ queryKey: ["vehicles"], queryFn: () => vehiclesApi.list({ pageSize: 200 }) });

  const save = useMutation({
    mutationFn: async () => {
      if (form.id) {
        await vehiclesApi.update(form.id, {
          id: form.id, registrationNo: form.registrationNo,
          carrierId: form.carrierId || undefined,
          capacityKg: form.capacityKg, coldChainCapable: form.coldChainCapable,
          insuranceExpiry: form.insuranceExpiry || undefined,
          isActive: form.isActive,
        });
      } else {
        await vehiclesApi.create({
          registrationNo: form.registrationNo,
          carrierId: form.carrierId || undefined,
          capacityKg: form.capacityKg, coldChainCapable: form.coldChainCapable,
          insuranceExpiry: form.insuranceExpiry || undefined,
        });
      }
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["vehicles"] }); setOpen(false); },
    onError: (e) => setError(errorMessage(e)),
  });
  const del = useMutation({
    mutationFn: (id: string) => vehiclesApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["vehicles"] }),
    onError: (e) => setError(errorMessage(e)),
  });

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Vehicles</Typography>
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => { setForm(blank()); setError(null); setOpen(true); }}>
          New vehicle
        </Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <Paper sx={{ p: 2 }}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Registration</TableCell><TableCell>Carrier</TableCell>
            <TableCell align="right">Capacity (kg)</TableCell><TableCell>Cold chain</TableCell>
            <TableCell>Insurance</TableCell><TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {query.data?.items.map(v => (
              <TableRow key={v.id} hover>
                <TableCell><b>{v.registrationNo}</b></TableCell>
                <TableCell>{v.carrierName ?? "—"}</TableCell>
                <TableCell align="right">{v.capacityKg}</TableCell>
                <TableCell>{v.coldChainCapable && <Chip size="small" icon={<AcUnitIcon />} label="Cold" color="info" />}</TableCell>
                <TableCell>{v.insuranceExpiry ? new Date(v.insuranceExpiry).toLocaleDateString() : "—"}</TableCell>
                <TableCell>
                  <Chip size="small" color={v.isActive ? "success" : "default"}
                    label={v.isActive ? "Active" : "Inactive"} />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => {
                    setForm({
                      id: v.id, registrationNo: v.registrationNo,
                      carrierId: v.carrierId ?? "", capacityKg: v.capacityKg,
                      coldChainCapable: v.coldChainCapable,
                      insuranceExpiry: v.insuranceExpiry ?? "",
                      isActive: v.isActive,
                    });
                    setError(null); setOpen(true);
                  }}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" color="error"
                    onClick={() => { if (confirm(`Delete vehicle ${v.registrationNo}?`)) del.mutate(v.id); }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{form.id ? `Edit vehicle ${form.registrationNo}` : "New vehicle"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Registration No *" value={form.registrationNo}
              onChange={(e) => setForm(f => ({ ...f, registrationNo: e.target.value }))} />
            <TextField select label="Carrier" value={form.carrierId}
              onChange={(e) => setForm(f => ({ ...f, carrierId: e.target.value }))}>
              <MenuItem value="">— none —</MenuItem>
              {carriers.data?.items.map(c => (
                <MenuItem key={c.id} value={c.id}>{c.code} — {c.name}</MenuItem>
              ))}
            </TextField>
            <TextField type="number" label="Capacity (kg)" value={form.capacityKg}
              onChange={(e) => setForm(f => ({ ...f, capacityKg: Number(e.target.value) || 0 }))}
              inputProps={{ min: 0, step: "0.1" }} />
            <TextField type="date" label="Insurance expiry" InputLabelProps={{ shrink: true }}
              value={form.insuranceExpiry}
              onChange={(e) => setForm(f => ({ ...f, insuranceExpiry: e.target.value }))} />
            <FormControlLabel
              control={<Switch checked={form.coldChainCapable}
                onChange={(e) => setForm(f => ({ ...f, coldChainCapable: e.target.checked }))} />}
              label="Cold chain capable"
            />
            {form.id && (
              <FormControlLabel
                control={<Switch checked={form.isActive}
                  onChange={(e) => setForm(f => ({ ...f, isActive: e.target.checked }))} />}
                label={form.isActive ? "Active" : "Inactive"}
              />
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!form.registrationNo || save.isPending}
            onClick={() => save.mutate()}>{form.id ? "Save" : "Create"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
